import { useState } from 'react'
import { router } from '@inertiajs/react'
import { useAuth } from '@/components/Layout'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import {
  LogOut,
  Sun,
  Moon,
  ChevronDown,
  GraduationCap,
  School,
} from 'lucide-react'

export function UserMenu() {
  const { user } = useAuth()
  const [themeMode, setThemeMode] = useState<'light' | 'dark'>(
    user?.school?.theme_mode || 'light',
  )
  const [open, setOpen] = useState(false)

  const toggleTheme = () => {
    const newMode = themeMode === 'dark' ? 'light' : 'dark'
    setThemeMode(newMode)

    const html = document.documentElement
    if (newMode === 'dark') {
      html.classList.add('dark')
    } else {
      html.classList.remove('dark')
    }

    fetch('/settings', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token':
          document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute('content') || '',
      },
      body: JSON.stringify({ school: { theme_mode: newMode } }),
    }).catch(console.error)
  }

  const handleLogout = () => {
    setOpen(false)
    router.delete('/sign_out')
  }

  const schoolAvatar = user?.school?.avatar_url
  const schoolName = user?.school?.name
  const initials = schoolName
    ? schoolName
        .split(' ')
        .map((n: string) => n[0])
        .slice(0, 2)
        .join('')
        .toUpperCase()
    : user?.email?.substring(0, 2).toUpperCase() || 'U'

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className="flex items-center gap-2 h-9 px-2 rounded-full"
        >
          {schoolAvatar ? (
            <img
              src={schoolAvatar}
              alt={schoolName || 'Logo'}
              className="h-7 w-7 rounded-full object-cover border border-border"
            />
          ) : (
            <Avatar className="h-7 w-7">
              <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
          )}
          <ChevronDown
            className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="end">
        {/* User Info */}
        <div className="p-4 pb-3">
          <div className="flex items-center gap-3">
            {schoolAvatar ? (
              <img
                src={schoolAvatar}
                alt={schoolName || 'Logo'}
                className="h-10 w-10 rounded-full object-cover border border-border"
              />
            ) : (
              <Avatar className="h-10 w-10">
                <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                  {initials}
                </AvatarFallback>
              </Avatar>
            )}
            <div className="flex-1 min-w-0">
              {schoolName && (
                <p className="text-sm font-medium truncate">{schoolName}</p>
              )}
              <p className="text-xs text-muted-foreground truncate">
                {user?.email}
              </p>
              <p className="text-xs text-muted-foreground capitalize">
                {user?.role}
              </p>
            </div>
          </div>
        </div>

        <Separator />

        {/* Theme Toggle */}
        <div className="p-3">
          <div className="flex items-center justify-between">
            <Label
              htmlFor="header-theme"
              className="text-sm cursor-pointer flex items-center gap-2"
            >
              {themeMode === 'dark' ? (
                <>
                  <Moon className="h-4 w-4" /> Modo Escuro
                </>
              ) : (
                <>
                  <Sun className="h-4 w-4" /> Modo Claro
                </>
              )}
            </Label>
            <Switch
              id="header-theme"
              checked={themeMode === 'dark'}
              onCheckedChange={toggleTheme}
            />
          </div>
        </div>

        <Separator />

        {/* Perfil da Escola — saiu do menu lateral e mora aqui, colado na
            identificação da escola que aparece no topo deste popover. */}
        <div className="p-2 pb-0">
          <Button
            variant="ghost"
            className="w-full justify-start gap-2"
            onClick={() => {
              setOpen(false)
              router.visit('/school/profile')
            }}
          >
            <School className="h-4 w-4" />
            Perfil da Escola
          </Button>
        </div>

        {/* Tutoriais */}
        <div className="p-2">
          <Button
            variant="ghost"
            className="w-full justify-start gap-2"
            onClick={() => {
              setOpen(false)
              router.visit('/tutorials')
            }}
          >
            <GraduationCap className="h-4 w-4" />
            Tutoriais
          </Button>
        </div>

        <Separator />

        {/* Logout */}
        <div className="p-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-start gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <LogOut className="h-4 w-4" />
                Sair
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirmar Saída</AlertDialogTitle>
                <AlertDialogDescription>
                  Tem certeza que deseja sair da sua conta?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={handleLogout}>
                  Sair
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </PopoverContent>
    </Popover>
  )
}
